import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { cachedLeaderboardAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import PersonIcon from '@mui/icons-material/Person';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const PersonalRanking = ({ currentUser }) => {
  const { t } = useTranslation(['leaderboard', 'common']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rankings, setRankings] = useState([]);
  const theme = useTheme();

  const fetchUserRanking = useCallback(async () => {
    if (!currentUser || !currentUser._id) {
      console.error('Пользователь не определен или не имеет ID:', currentUser);
      setError(t('leaderboard:personal.error.userNotDefined'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await cachedLeaderboardAPI.getUserRanking(currentUser._id, 2);

      // Проверяем как на полный путь данных (response.data.rankings), так и на rankings напрямую
      const rankingsData =
        response.data?.rankings ||
        (Array.isArray(response.rankings) ? response.rankings : []);

      if (rankingsData && rankingsData.length > 0) {
        setRankings(rankingsData);
        setError(null);
      } else {
        setRankings([]);
        setError(t('leaderboard:personal.error.emptyResponse'));
      }
    } catch (apiError) {
      console.error('Ошибка API:', apiError);
      setError(t('leaderboard:personal.error.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentUser, t]);

  useEffect(() => {
    // Делаем запрос только если ID пользователя определен
    const userId = currentUser?._id;

    if (userId) {
      fetchUserRanking();
    }
  }, [currentUser, fetchUserRanking, loading]);

  // Функция для получения локализованного названия роли
  const getRoleLabel = useCallback((roles) => {
    const role = roles.includes('admin') ? 'admin' : roles.includes('teacher') ? 'teacher' : 'student';
    switch (role) {
      case 'admin':
        return t('common:roles.admin');
      case 'teacher':
        return t('common:roles.teacher');
      default:
        return t('common:roles.student');
    }
  }, [t]);

  // Функция для получения цвета роли
  const getRoleColor = useCallback((roles) => {
    const role = roles.includes('admin') ? 'admin' : roles.includes('teacher') ? 'teacher' : 'student';
    switch (role) {
      case 'admin':
        return 'error';
      case 'teacher':
        return 'primary';
      default:
        return 'success';
    }
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box my={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!rankings || rankings.length === 0) {
    return (
      <Box my={3}>
        <Alert severity="info">
          {t('leaderboard:personal.error.noData')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h5"
        mb={3}
        display="flex"
        alignItems="center"
        gap={1}
      >
        <PersonIcon />
        {t('leaderboard:personal.title')}
      </Typography>

      <Paper
        elevation={0}
        variant="outlined"
        sx={{ borderRadius: '16px', p: 0, overflow: 'hidden' }}
      >
        {rankings.map((user, index) => {
          const isCurrentUser = user._id === currentUser._id;
          const rankChange = user.rankChange || 0;

          return (
            <Box
              key={user._id}
              component={Link}
              to={`/profile/${user._id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                borderBottom:
                  index < rankings.length - 1
                    ? '1px solid rgba(0,0,0,0.1)'
                    : 'none',
                bgcolor: isCurrentUser
                  ? 'rgba(66, 165, 245, 0.1)'
                  : 'transparent',
                '&:hover': {
                  bgcolor: isCurrentUser
                    ? 'rgba(66, 165, 245, 0.15)'
                    : 'rgba(0,0,0,0.02)',
                },
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <Typography
                variant="h6"
                width="40px"
                fontWeight="bold"
                color={isCurrentUser ? 'primary' : 'text.secondary'}
              >
                {user.rank}
              </Typography>

              <Avatar
                src={user.userMeta.avatar}
                sx={{
                  width: 40,
                  height: 40,
                  mr: 2,
                  border: isCurrentUser
                    ? `2px solid ${theme.palette.primary.main}`
                    : 'none',
                }}
              />

              <Box flexGrow={1}>
                <Typography
                  variant={isCurrentUser ? 'subtitle1' : 'body1'}
                  fontWeight={isCurrentUser ? 'bold' : 'normal'}
                >
                  {user.userMeta.fullName}
                  {isCurrentUser && ` (${t('common:you')})`}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                    mr: 1,
                  }}
                >
                  <Chip
                    label={getRoleLabel(user.userMeta.roles)}
                    color={getRoleColor(user.userMeta.roles)}
                    size="small"
                  />
                </Typography>
              </Box>

              <Box display="flex" alignItems="center">
                {rankChange !== 0 && (
                  <Box
                    display="flex"
                    alignItems="center"
                    mr={2}
                    color={rankChange > 0 ? 'success.main' : 'error.main'}
                  >
                    {rankChange > 0 ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={rankChange > 0 ? 'success.main' : 'error.main'}
                    >
                      {Math.abs(rankChange)}
                    </Typography>
                  </Box>
                )}

                <Typography variant="h6" fontWeight="bold" color="primary">
                  {user.experience} XP
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default PersonalRanking;
